/**
 * Frame-time overlay for measuring on real devices: open the game with `?stats=1`.
 * Shows frames per second, the slowest recent frame, and where the main thread spends
 * each frame. Rendering commands are queued to the GPU asynchronously, so a smooth
 * "render" number with a low fps means the GPU, not the script, is the limit.
 */
export interface StatsSample { update: number; render: number; present: number; frame: number }

export class Stats {
  private el: HTMLElement;
  private samples: StatsSample[] = [];
  private lastReport = 0;
  private worst = 0;
  private slow = 0;
  private total = 0;

  constructor(parent: HTMLElement = document.body) {
    this.el = document.createElement('pre');
    this.el.id = 'stats';
    this.el.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:20;margin:0;padding:6px 8px;background:rgba(0,0,0,0.7);color:#9f9;font:12px/1.4 monospace;pointer-events:none;white-space:pre';
    parent.appendChild(this.el);
    this.el.textContent = `gpu: ${gpuName()}\n측정 중…`;
  }

  push(s: StatsSample): void {
    this.samples.push(s);
    this.total++;
    if (s.frame > this.worst) this.worst = s.frame;
    if (s.frame > 1000 / 45) this.slow++;
    const now = performance.now();
    if (now - this.lastReport < 500 || !this.samples.length) return;
    this.lastReport = now;
    const n = this.samples.length;
    const avg = (k: keyof StatsSample) => (this.samples.reduce((a, x) => a + x[k], 0) / n).toFixed(2);
    const fps = (1000 / (this.samples.reduce((a, x) => a + x.frame, 0) / n)).toFixed(0);
    this.el.textContent = [
      `gpu: ${gpuName()}`,
      `fps ${fps}   worst frame ${this.worst.toFixed(1)} ms   slow frames ${(100 * this.slow / this.total).toFixed(1)}%`,
      `script per frame: update ${avg('update')}  render ${avg('render')}  present ${avg('present')} ms`,
      `${window.innerWidth}x${window.innerHeight} @${devicePixelRatio}x  ${(navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? '?'}GB  ${navigator.hardwareConcurrency ?? '?'} cores`,
    ].join('\n');
    this.samples.length = 0;
  }
}

function gpuName(): string {
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    const info = gl?.getExtension('WEBGL_debug_renderer_info');
    return info ? String(gl!.getParameter(info.UNMASKED_RENDERER_WEBGL)).replace(/^ANGLE \((.*)\)$/, '$1').slice(0, 60) : 'unknown';
  } catch {
    return 'unknown';
  }
}
